import { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Report, factories, IEmbedConfiguration, service, models } from 'powerbi-client';
import { reportsService } from '../../../service/reports.service';

export interface ReportFilter {
  table: string;
  column: string;
  value: string;
}

interface PowerBIReportProps {
  reportName: string;
  filter?: ReportFilter;
  loadingText?: string;
}

const powerbiGlobalService = new service.Service(
  factories.hpmFactory,
  factories.wpmpFactory,
  factories.routerFactory
);

const PowerBIReport = ({ reportName, filter, loadingText = 'Loading report...' }: PowerBIReportProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reportContainer = useRef<HTMLDivElement>(null);

  const fTable = filter?.table;
  const fColumn = filter?.column;
  const fValue = filter?.value;

  useEffect(() => {
    const container = reportContainer.current;
    if (!container) return;

    let cancelled = false;

    const buildFilter = (): models.IBasicFilter | null => {
      if (!fTable || !fColumn || !fValue) return null;
      return {
        $schema: 'http://powerbi.com/product/schema#basic',
        target: { table: fTable, column: fColumn },
        operator: 'In',
        values: [fValue],
        filterType: models.FilterType.Basic
      };
    };

    const embedReport = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await reportsService.getEmbedToken(reportName);
        if (cancelled) return;

        powerbiGlobalService.reset(container);

        const basicFilter = buildFilter();

        const config: IEmbedConfiguration = {
          type: 'report',
          id: data.reportId,
          embedUrl: data.embedUrl,
          accessToken: data.accessToken,
          tokenType: models.TokenType.Embed,
          filters: basicFilter ? [basicFilter] : undefined,
          settings: {
            panes: {
              filters: { visible: false },
              pageNavigation: { visible: false }
            },
            background: models.BackgroundType.Transparent
          }
        };

        const report = powerbiGlobalService.embed(container, config) as Report;

        report.off('loaded');
        report.on('loaded', async () => {
          try {
            const f = buildFilter();
            if (f) {
              await report.updateFilters(models.FiltersOperations.ReplaceAll, [f]);
            }
          } catch (e) {
            console.error('setFilters failed:', e);
          } finally {
            if (!cancelled) setLoading(false);
          }
        });

        report.off('rendered');
        report.on('rendered', () => {
          if (!cancelled) setLoading(false);
        });

        report.off('error');
        report.on('error', (event: any) => {
          console.error('PowerBI error:', event.detail);
          if (!cancelled) {
            setError('Error loading Power BI Report');
            setLoading(false);
          }
        });
      } catch (err: any) {
        console.error('Error in embedReport:', err);
        if (!cancelled) {
          setError('Error loading Power BI Report');
          setLoading(false);
        }
      }
    };

    embedReport();

    return () => {
      cancelled = true;
      powerbiGlobalService.reset(container);
    };
  }, [reportName, fTable, fColumn, fValue]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: 'transparent' }}>
      <div
        ref={reportContainer}
        style={{ width: '100%', height: '100%', display: loading || error ? 'none' : 'block' }}
      />

      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.8)', zIndex: 2 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography color="textSecondary">{loadingText}</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, zIndex: 2 }}>
          <Typography color="error" variant="h6">{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default PowerBIReport;